/**
 * Which language to render in, decided once for every host.
 *
 * ---------------------------------------------------------------------------
 * THE POLICY
 * ---------------------------------------------------------------------------
 *
 * Most specific wins, and the order is the same everywhere:
 *
 *   1. **The host says so.** A `?lang=` on an embed, the locale a WordPress page
 *      was rendered in. Not a preference — a fact about the page, and it beats
 *      everything, including a choice the reader made somewhere else.
 *   2. **The reader chose, here, before.** A stored choice from this app's own
 *      switcher.
 *   3. **The browser.** `navigator.languages`, first supported match.
 *   4. **A fallback**, because a browser can ask for a language nothing here
 *      speaks.
 *
 * ---------------------------------------------------------------------------
 * WHY THE BROWSER RATHER THAN A DEFAULT NOBODY PICKED
 * ---------------------------------------------------------------------------
 *
 * Both apps used to fall back to Dutch, hardcoded. That is right for exactly one
 * park and wrong for every other, and it made the first thing an English-speaking
 * operator did on every new machine a trip to a dropdown. The browser has already
 * been told what its owner reads; asking it is free.
 *
 * ---------------------------------------------------------------------------
 * WHY "AUTO" HAS TO BE A CHOICE YOU CAN MAKE
 * ---------------------------------------------------------------------------
 *
 * Storing a language the moment somebody looks at the switcher is a decision they
 * cannot undo: from then on the app ignores their browser forever, on that
 * machine, with nothing on screen to say why. So `auto` is a value the switcher
 * can be set BACK to, and it is stored as the absence of a choice rather than as
 * the language it currently resolves to — otherwise a reader who moves country
 * keeps being addressed in the language they had a year ago.
 */

/** `auto` means "no choice recorded"; anything else is an explicit one. */
export type LanguageChoice<T extends string> = T | 'auto';

export interface LanguageInputs<T extends string> {
  /** Languages this build actually has catalogues for. */
  supported: readonly T[];
  /** Used when nothing else resolves. The source language is the safe floor. */
  fallback: T;
  /**
   * Stated by the host: a query parameter, a page's locale. Wins outright.
   *
   * A locale is accepted as well as a language — `nl_NL` and `nl-BE` both mean
   * `nl` here, because a park running a Belgian site does not want its own
   * catalogue.
   */
  host?: string | null;
  /**
   * A host's own preference ORDER, tried after `host` and before anything else.
   *
   * WordPress multilingual plugins know which languages a site publishes and in
   * what order; a park's own settings may list more than one. Passing the list
   * rather than only the winner means a site whose current page is in a language
   * this build lacks still lands on the site's second choice instead of on the
   * reader's browser, which is a different park's answer.
   */
  hostOrder?: readonly string[] | null;
  /** What the reader chose in this app before. `auto` and null both mean none. */
  chosen?: LanguageChoice<T> | null;
  /** Normally `navigator.languages`. Passed in so this stays testable. */
  browser?: readonly string[];
}

/** `nl_NL`, `nl-BE`, `NL` → `nl`. Anything unusable → null. */
export function languageOf(tag: string | null | undefined): string | null {
  if (!tag) return null;
  const two = tag.trim().slice(0, 2).toLowerCase();
  return /^[a-z]{2}$/.test(two) ? two : null;
}

/**
 * The supported language a tag names, else null.
 *
 * Exported because a caller parsing a URL needs the same leniency the policy has:
 * a host handing us `nl-NL` in a query parameter means Dutch, and rejecting it for
 * not being exactly `nl` would silently fall through to the browser.
 */
export function matchLanguage<T extends string>(
  tag: string | null | undefined, supported: readonly T[],
): T | null {
  return match(tag, supported);
}

function match<T extends string>(tag: string | null | undefined, supported: readonly T[]): T | null {
  const want = languageOf(tag);
  if (!want) return null;
  return supported.find((s) => s.toLowerCase() === want) ?? null;
}

/** Resolve the policy above. Pure, so a test can state each rung of it. */
export function resolveLanguage<T extends string>(inputs: LanguageInputs<T>): T {
  const { supported, fallback, host, hostOrder, chosen, browser } = inputs;

  const stated = match(host, supported);
  if (stated) return stated;

  for (const tag of hostOrder ?? []) {
    const found = match(tag, supported);
    if (found) return found;
  }

  if (chosen && chosen !== 'auto') {
    const picked = match(chosen, supported);
    // A stored value for a language this build no longer has is ignored rather
    // than honoured into a crash — which is what happens when a catalogue is
    // removed and somebody's browser still holds its name.
    if (picked) return picked;
  }

  for (const tag of browser ?? []) {
    const found = match(tag, supported);
    if (found) return found;
  }

  return fallback;
}

/** `navigator.languages` where there is one, in an order this can rely on. */
export function browserLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return [];
  const nav = navigator as Navigator & { languages?: readonly string[]; language?: string };
  if (Array.isArray(nav.languages) && nav.languages.length) return nav.languages;
  return nav.language ? [nav.language] : [];
}

/**
 * Read a stored choice.
 *
 * Storage throws outright in some private modes, so every access is guarded. A
 * reader in private browsing gets the browser's language, which is the right
 * answer rather than a failure.
 */
export function readChoice<T extends string>(
  key: string, supported: readonly T[],
): LanguageChoice<T> | null {
  try {
    const v = localStorage.getItem(key);
    if (!v || v === 'auto') return v === 'auto' ? 'auto' : null;
    return supported.find((s) => s === v) ?? null;
  } catch {
    return null;
  }
}

/** Store a choice, or record that there is none. */
export function writeChoice(key: string, choice: string): void {
  try { localStorage.setItem(key, choice); } catch { /* private mode */ }
}
